/**
 * Bridge so non-React code (the push-notification tap handler in push.ts)
 * can ask App.tsx to open a specific game room.
 *
 * Safety: App.tsx validates the room is still live (via isRoomLive) BEFORE
 * entering, and falls back to the hub for a room that has finished, been
 * abandoned, or aged out — so a stale "your turn" push never drops the user
 * into a dead room.
 *
 * The last intent is buffered so a cold launch (the tap event can fire before
 * App.tsx subscribes) isn't lost — App consumes it once on mount.
 */
import { supabase } from './supabase'

export type RoomGame = 'iet' | 'colorful' | 'three_step' | 'fake_out' | 'close_enough'
export interface RoomNavIntent { game: RoomGame; roomId: string }

/** Non-room destinations a push can point at (mapped from data.type). */
export type AppScreenTarget = 'profile' | 'weekly'

let pending: RoomNavIntent | null = null
let pendingScreen: AppScreenTarget | null = null
const listeners = new Set<() => void>()

function notify(): void {
  listeners.forEach((l) => { try { l() } catch { /* ignore */ } })
}

/** Called by the push tap handler. Buffers + notifies any subscriber. */
export function requestRoomNav(intent: RoomNavIntent): void {
  pending = intent
  pendingScreen = null // a room intent is more specific; don't double-navigate
  notify()
}

/** Push tap on a non-room notification (trophy → profile, weekly → weekly). */
export function requestScreenNav(target: AppScreenTarget): void {
  pendingScreen = target
  notify()
}

/** App.tsx takes (and clears) the pending intent. */
export function consumeRoomNav(): RoomNavIntent | null {
  const intent = pending
  pending = null
  return intent
}

/** App.tsx takes (and clears) the pending screen target. */
export function consumeScreenNav(): AppScreenTarget | null {
  const target = pendingScreen
  pendingScreen = null
  return target
}

export function subscribeRoomNav(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

// Statuses that mean the room can no longer be entered, per game.
const DEAD_STATUS: Record<RoomGame, string[]> = {
  iet:        ['results', 'abandoned'],
  colorful:   ['finished', 'abandoned'],
  three_step: ['results', 'abandoned'],
  fake_out:     ['finished', 'abandoned'],
  close_enough: ['finished', 'abandoned'],
}
const ROOM_TABLE: Record<RoomGame, string> = {
  iet:        'rooms',
  colorful:   'colorful_rooms',
  three_step: 'three_step_rooms',
  fake_out:     'word_endless_rooms',
  close_enough: 'word_endless_rooms',
}
/**
 * Word rooms key on their 6-char code, not a uuid, so the liveness probe
 * matches on session_id — which is what list_my_active_rooms hands back
 * as room_id for those games.
 */
const ROOM_ID_COL: Record<RoomGame, string> = {
  iet: 'id', colorful: 'id', three_step: 'id',
  fake_out: 'session_id', close_enough: 'session_id',
}

/**
 * True only if the room exists, is visible to this user (RLS), and hasn't
 * ended. Missing / not-a-member / errored all resolve to false so the caller
 * safely falls back rather than entering a dead room.
 */
export async function isRoomLive(game: RoomGame, roomId: string): Promise<boolean> {
  try {
    const { data } = await (supabase as any)
      .from(ROOM_TABLE[game])
      .select('status')
      .eq(ROOM_ID_COL[game], roomId)
      .maybeSingle()
    if (!data?.status) return false
    return !DEAD_STATUS[game].includes(data.status as string)
  } catch {
    return false
  }
}
